import { CommonModule } from '@angular/common';
import {
  Component,
  ErrorHandler,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { BnNgIdleService } from 'bn-ng-idle';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SessionValues } from '../models/api/core/configs/session-values';
import { DialogFocusService } from '../services/a11y/dialog-focus.service';
import { Api } from '../services/api/api.service';
import { ErrorHandlerService } from '../services/error-handler/error-handler.service';
import { Session } from '../services/session/session.service';
import { Dispose } from '../utilities/dispose';
import { ScrollTopButtonComponent } from './core/buttons/scroll-top-button.component';
import { TimeoutDialogComponent } from './layout/timeout.component';
import { removeOrphanedOverlays } from '../utilities/overlay-cleanup';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ReactiveFormsModule,
    CommonModule,
    ScrollTopButtonComponent,
  ],
  providers: [DialogService],
  template: ` <router-outlet></router-outlet><app-scroll-top-button />`,
  styles: ``,
})
export class AppComponent implements OnInit, OnDestroy {
  session = inject(Session);
  private bnIdle = inject(BnNgIdleService);
  dialogService = inject(DialogService);
  private api = inject(Api);
  private errorHandler = inject(ErrorHandler);
  private dialogFocus = inject(DialogFocusService);

  dialogRef: DynamicDialogRef | undefined;
  alertTime: number;
  logoutThreshold: number;
  warnThreshold: number;
  abandonThreshold: number;
  abandonWarnThreshold: number;
  sessionValues: SessionValues;
  dispose = new Dispose();

  events = ['mousemove', 'keypress'];

  async ngOnInit() {
    void this.setThresholds();
    this.dispose.subs = this.session.updateUser.subscribe(() => {
      void this.setThresholds();
    });
    this.dispose.subs = this.session.updateActivity.subscribe(
      (extendSession: boolean) => {
        if (extendSession) {
          this.doUpdateActivity(true);
        } else {
          this.doLogout(false);
        }
      },
    );

    this.dispose.subs = this.session.sessionExpiredLogout.subscribe(() => {
      this.doLogout(true);
    });

    this.dispose.subs = this.session.sessionLifetimeOkPressed.subscribe(() => {
      this.sessionLifetimeOk(true);
    });

    this.dispose.subs = this.session.maxSessionExpired.subscribe(() => {
      this.doLogout(true);
    });

    this.dispose.subs = this.bnIdle
      .startWatching(60)
      .subscribe((isCheckTimeout: boolean) => {
        if (!document.location.pathname.includes('/login')) {
          this.checkMaxSessionLifetime();
        }

        if (isCheckTimeout && !document.location.pathname.includes('/login')) {
          this.checkInactivityTimeout();
        } else {
          this.doUpdateActivity(true);
        }
      });

    // standalone angular requires global event listeners
    this.addUserActivityListeners();
  }

  private async setThresholds() {
    const config = await this.api.configs.get();
    this.sessionValues = config.session;
    this.warnThreshold = parseInt(this.sessionValues.warnThreshold);
    this.logoutThreshold = parseInt(this.sessionValues.logoutThreshold);
    this.abandonWarnThreshold = parseInt(
      this.sessionValues.abandonWarnThreshold,
    );
    this.abandonThreshold = parseInt(this.sessionValues.abandonLogoutThreshold);
    this.startInternalUserLogoutFallback();
  }

  private internalUserFallbackStarted = false;

  private startInternalUserLogoutFallback() {
    if (this.internalUserFallbackStarted) return;
    if (!this.logoutThreshold) return;
    this.internalUserFallbackStarted = true;

    this.dispose.intervals = setInterval(() => {
      if (document.location.pathname.includes('/login')) return;
      if (!this.logoutThreshold) return;
      if (this.session.user?.isClient) return;

      const elapsed = (Date.now() - this.session.lastActive.getTime()) / 1000;
      if (elapsed > this.logoutThreshold) {
        this.doLogout(true);
      }
    }, 30000);
  }

  private isInMaxSessionWarningZone(): boolean {
    const elapsed = (Date.now() - this.session.firstActive.getTime()) / 1000;
    return (
      this.abandonWarnThreshold > 0 && elapsed >= this.abandonWarnThreshold
    );
  }

  private checkInactivityTimeout() {
    if (this.isInMaxSessionWarningZone()) {
      return;
    }

    const dt = new Date();
    const lastDt = this.session.lastActive;
    this.alertTime = this.logoutThreshold - this.warnThreshold;
    const ms = dt.getTime() - lastDt.getTime();
    const d = ms / 1000;

    if (
      this.warnThreshold > 0 &&
      d >= this.warnThreshold &&
      d <= this.logoutThreshold
    ) {
      if (!this.session.showPopup) {
        this.dialogRef = this.dialogFocus.openWithFocus(
          this.dialogService,
          TimeoutDialogComponent,
          {
            header: 'Your session is about to close',
            width: '30rem',
            modal: true,
            inputValues: {
              countFrom: this.logoutThreshold - this.warnThreshold,
              inactivityDialog: true,
              message: `If you are still working, simply click 'OK' to continue.`,
              minutes: '5',
              seconds: '00',
            },
          },
        );
      }
      this.session.showPopup = true;
    }

    if (d > this.logoutThreshold) {
      this.doUpdateActivity(false);
      this.doLogout(true);
    }
  }

  private checkMaxSessionLifetime() {
    const dt = new Date();
    const msx = dt.getTime() - this.session.firstActive.getTime();
    const dx = msx / 1000;

    if (dx >= this.abandonWarnThreshold && !this.session.maxSessionOkPressed) {
      if (!this.session.showPopup) {
        this.dialogRef = this.dialogFocus.openWithFocus(
          this.dialogService,
          TimeoutDialogComponent,
          {
            header:
              'You have reached the maximum timeframe for an active session.',
            width: '30rem',
            modal: true,
            inputValues: {
              countFrom: Math.floor(this.abandonThreshold - dx),
              inactivityDialog: false,
              message: `You will be redirected to the login screen.
                  To begin a new session, please login again.`,
              minutes: Math.floor(
                Math.floor(this.abandonThreshold - dx) / 60,
              ).toString(),
              seconds: (Math.floor(this.abandonThreshold - dx) % 60)
                .toString()
                .padStart(2, '0'),
            },
          },
        );
      }
      this.session.showPopup = true;
    }

    if (dx > this.abandonThreshold) {
      this.doLogout(true);
    }
  }

  public doUpdateActivity(hidePopup: boolean) {
    this.session.showPopup = !hidePopup;
    this.dialogRef?.close();
    this.session.lastActive = new Date();
    this.api.session.active();
  }

  public sessionLifetimeOk(hidePopup: boolean) {
    this.session.showPopup = !hidePopup;
    this.session.maxSessionOkPressed = true;
    this.dialogRef?.close();

    // Dialog countdown is destroyed on close; keep an exact logout deadline.
    const remainingMs =
      this.session.firstActive.getTime() +
      this.abandonThreshold * 1000 -
      Date.now();
    if (remainingMs <= 0) {
      void this.doLogout(true);
      return;
    }
    this.dispose.timeouts = setTimeout(() => this.doLogout(true), remainingMs);
  }

  private async doLogout(expired: boolean) {
    this.dialogRef?.close();
    (this.errorHandler as ErrorHandlerService).closeSensitiveClaimDialog();
    removeOrphanedOverlays();
    await this.session.logout(expired);
  }

  private handleUserActivity = () => {
    if (!this.session.showPopup) {
      this.bnIdle.resetTimer();
      this.doUpdateActivity(true);
    }
  };

  private handleVisibilityChange = () => {
    if (
      document.visibilityState === 'visible' &&
      !document.location.pathname.includes('/login')
    ) {
      this.checkMaxSessionLifetime();
      this.checkInactivityTimeout();
    }
  };

  private addUserActivityListeners() {
    this.events.forEach((event) => {
      document.addEventListener(event, this.handleUserActivity, true);
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private removeUserActivityListeners() {
    this.events.forEach((event) => {
      document.removeEventListener(event, this.handleUserActivity, true);
    });
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }

  ngOnDestroy(): void {
    this.removeUserActivityListeners();
    this.dispose.dispose();
  }
}
